import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, take } from 'rxjs';
import { ManageRolesComponent } from '../manage-roles/manage-roles.component';
import { OrganizationSettingsComponent } from '../user-setting/organization-settings/organization-settings.component';
import { SmtpSettingsComponent } from '../user-setting/smtp-settings/smtp-settings.component';
import { EmailConfigurationComponent } from '../user-setting/email-configuration/email-configuration.component';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { AdminAccessService } from 'src/app/core/services/admin-access.service';
import {
  RbacPortalScopeService,
  type RbacPortalScope,
} from 'src/app/core/services/rbac-portal-scope.service';
import {
  UserAccountApiService,
  type SimulateAgentMethod,
} from 'src/app/core/services/user-account-api.service';
import { AdminSessionProfileService } from 'src/app/core/services/admin-session-profile.service';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { PermissionService } from 'src/app/core/services/permission.service';

/** Form value: empty string means unset (first-time / cleared). */
type SimulateAgentMethodForm = Exclude<SimulateAgentMethod, null> | '';

type SystemPreferencesTab =
  | 'manage-roles'
  | 'stimulate-agent'
  | 'smtp'
  | 'tenant-email'
  | 'organization';

@Component({
  selector: 'app-system-preferences',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    IconModule,
    PageSurfaceHeaderComponent,
    ManageRolesComponent,
    OrganizationSettingsComponent,
    SmtpSettingsComponent,
    EmailConfigurationComponent,
    HasPermissionPipe,
  ],
  templateUrl: './system-preferences.component.html',
})
export class SystemPreferencesComponent implements OnInit {
  private readonly adminAccess = inject(AdminAccessService);
  private readonly userApi = inject(UserAccountApiService);
  private readonly sessionProfile = inject(AdminSessionProfileService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);
  readonly rbacScope = inject(RbacPortalScopeService);
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  activeTab: SystemPreferencesTab = 'manage-roles';

  readonly isSuperAdmin = this.adminAccess.isSuperAdmin;
  readonly profileLoaded = this.adminAccess.profileLoaded;

  /** Empty string means unset (first-time / cleared). */
  simulateAgentMethod: SimulateAgentMethodForm = '';
  savedMethod: SimulateAgentMethodForm = '';
  methodLoading = false;
  saving = false;
  loadError = '';
  saveError = '';

  ngOnInit(): void {
    this.adminAccess.ensureProfileLoaded();
    this.route.queryParamMap.subscribe((params) => {
      const requested = params.get('section') ?? params.get('tab');
      this.activeTab = this.resolveActiveTab(this.parseTab(requested));
      if (this.activeTab === 'stimulate-agent') {
        this.loadSimulateAgentMethod();
      }
    });
  }

  setTab(tab: SystemPreferencesTab): void {
    if (!this.canAccessTab(tab)) {
      return;
    }
    this.activeTab = tab;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { section: tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    if (tab === 'stimulate-agent') {
      this.loadSimulateAgentMethod();
    }
  }

  canAccessTab(tab: SystemPreferencesTab): boolean {
    switch (tab) {
      case 'manage-roles':
        return this.permissionService.hasPermission(this.P.ROLE_LIST);
      case 'stimulate-agent':
        return this.permissionService.hasAnyPermission([
          this.P.SIMULATE_AGENT_SETTINGS_LIST,
          this.P.SIMULATE_AGENT_SETTINGS_UPDATE,
        ]);
      case 'smtp':
        return this.permissionService.hasAnyPermission([
          this.P.SMTP_SETTINGS_LIST,
          this.P.SMTP_SETTINGS_UPDATE,
        ]);
      case 'tenant-email':
        return this.permissionService.hasAnyPermission([
          this.P.EMAIL_TEMPLATE_LIST,
          this.P.EMAIL_TEMPLATE_UPDATE,
          this.P.EMAIL_TEMPLATE_TOGGLE,
        ]);
      case 'organization':
        return this.permissionService.hasAnyPermission([
          this.P.ORGANIZATION_DETAILS_LIST,
          this.P.ORGANIZATION_DETAILS_UPDATE,
        ]);
      default:
        return false;
    }
  }

  onPortalScopeChange(scope: RbacPortalScope): void {
    this.rbacScope.setScope(scope);
  }

  loadSimulateAgentMethod(): void {
    this.methodLoading = true;
    this.loadError = '';
    this.saveError = '';
    this.userApi
      .getAdminProfile()
      .pipe(
        assertSuccessEnvelope(),
        take(1),
        finalize(() => (this.methodLoading = false)),
      )
      .subscribe({
        next: (env) => {
          if (env.data) this.sessionProfile.applyFromProfileData(env.data);
          const m = env.data?.simulate_agent_method;
          this.simulateAgentMethod = m === 'ai' || m === 'idp' ? m : '';
          this.savedMethod = this.simulateAgentMethod;
        },
        error: (err) => {
          this.loadError =
            resolveApiErrorMessage(err) || 'Could not load Stimulate Agent settings.';
          this.simulateAgentMethod = '';
          this.savedMethod = '';
        },
      });
  }

  saveStimulateAgent(): void {
    this.saving = true;
    this.saveError = '';
    const value: SimulateAgentMethod =
      this.simulateAgentMethod === 'ai' || this.simulateAgentMethod === 'idp'
        ? this.simulateAgentMethod
        : null;
    this.userApi
      .updateSimulateAgentMethod(value)
      .pipe(
        assertSuccessEnvelope(),
        take(1),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (env) => {
          const m = env.data?.simulate_agent_method;
          this.simulateAgentMethod = m === 'ai' || m === 'idp' ? m : '';
          this.savedMethod = this.simulateAgentMethod;
          const prev = this.sessionProfile.getLastProfile();
          if (prev) {
            this.sessionProfile.applyFromProfileData({
              ...prev,
              simulate_agent_method: m === 'ai' || m === 'idp' ? m : null,
            });
          }
          this.snackBar.open('Stimulate Agent settings saved', 'Close', { duration: 3000 });
        },
        error: (err) => {
          this.saveError = resolveApiErrorMessage(err) || 'Could not save settings.';
        },
      });
  }

  get canSave(): boolean {
    return (
      !this.saving &&
      !this.methodLoading &&
      this.simulateAgentMethod !== this.savedMethod
    );
  }

  private parseTab(value: string | null): SystemPreferencesTab | null {
    if (
      value === 'manage-roles' ||
      value === 'stimulate-agent' ||
      value === 'smtp' ||
      value === 'tenant-email' ||
      value === 'organization'
    ) {
      return value;
    }
    return null;
  }

  private resolveActiveTab(requested: SystemPreferencesTab | null): SystemPreferencesTab {
    const order: SystemPreferencesTab[] = [
      'manage-roles',
      'stimulate-agent',
      'smtp',
      'tenant-email',
      'organization',
    ];
    if (requested && this.canAccessTab(requested)) {
      return requested;
    }
    return order.find((tab) => this.canAccessTab(tab)) ?? 'manage-roles';
  }
}
